<template>
    <div v-if="editor" class="editor-container">
        <div class="toolbar">
            <button @click="editor.chain().focus().toggleBold().run()"
                :class="{ 'is-active': editor.isActive('bold') }">
                <span class="toolbar-icon">B</span>
            </button>
            <button @click="editor.chain().focus().toggleItalic().run()"
                :class="{ 'is-active': editor.isActive('italic') }">
                <span class="toolbar-icon">I</span>
            </button>
            <button @click="editor.chain().focus().toggleUnderline().run()"
                :class="{ 'is-active': editor.isActive('underline') }">
                <span class="toolbar-icon">U</span>
            </button>
            <button @click="setLink()" :class="{ 'is-active': editor.isActive('link') }">
                <span class="toolbar-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                </span>
            </button>
            <button @click="editor.chain().focus().toggleBulletList().run()"
                :class="{ 'is-active': editor.isActive('bulletList') }">
                <span class="toolbar-icon">

                    <i class="fas fa-list-ul"></i>
                </span>
            </button>
            <button @click="editor.chain().focus().toggleOrderedList().run()"
                :class="{ 'is-active': editor.isActive('orderedList') }">
                <span class="toolbar-icon">

                    <i class="fas fa-list-ol"></i>
                </span>
            </button>
        </div>
        <editor-content :editor="editor" class="editor-content" />
    </div>
</template>

<script>
import ListItem from '@tiptap/extension-list-item'
import TextStyle from '@tiptap/extension-text-style'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { Editor, EditorContent } from '@tiptap/vue-2'

export default {
    components: {
        EditorContent,
    },
    props: {
        value: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            editor: null,
        }
    },
    mounted() {
        this.editor = new Editor({
            extensions: [
                TextStyle.configure({ types: [ListItem.name] }),
                StarterKit,
                Underline,
                Link.configure({
                    openOnClick: false,
                    defaultProtocol: 'https',
                }),
            ],
            content: this.value,
            onUpdate: () => {
                // Emit update event with new content
                this.$emit('input', this.editor.getHTML())
            }
        })
    },
    methods: {

        setLink() {
            const previousUrl = this.editor.getAttributes('link').href
            const url = window.prompt('URL', previousUrl)

            // cancelled
            if (url === null) {
                return
            }

            // empty
            if (url === '') {
                this.editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .unsetLink()
                    .run()

                return
            }

            // update link
            this.editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url })
                .run()
        },
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy()
        }
    }
}
</script>

<style lang="scss">
.editor-container {
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
}

.toolbar {
    display: flex;
    padding: 8px;
    background-color: #fff;
    border-bottom: 1px solid #e2e8f0;

    button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        margin-right: 4px;
        background: transparent;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        color: #4a5568;

        &:hover {
            background-color: #e2e8f0;
        }

        &.is-active {
            color: #2b6cb0;
            background-color: #e6f2ff;
        }
    }

    .toolbar-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px !important;
        font-weight: 500;
    }
}

.editor-content {
    padding: 16px;
    min-height: 200px;

    .ProseMirror {
        outline: none;

        p {
            margin: 0.5em 0;
        }

        &:focus {
            outline: none;
        }
    }
}

/* Basic editor styles */
.tiptap {
    ul {
        list-style-type: disc !important;
    }

    ol {
        list-style-type: decimal !important;
    }

    :first-child {
        margin-top: 0;
    }

    /* List styles */
    ul,
    ol {
        padding: 0 1rem;
        margin: 1.25rem 1rem 1.25rem 0.4rem;

        li p {
            margin-top: 0.25em;
            margin-bottom: 0.25em;
        }
    }

    /* Heading styles */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        line-height: 1.1;
        margin-top: 2.5rem;
        text-wrap: pretty;
    }

    h1,
    h2 {
        margin-top: 3.5rem;
        margin-bottom: 1.5rem;
    }

    h1 {
        font-size: 1.4rem;
    }

    h2 {
        font-size: 1.2rem;
    }

    h3 {
        font-size: 1.1rem;
    }

    h4,
    h5,
    h6 {
        font-size: 1rem;
    }

    /* Code and preformatted text styles */
    code {
        background-color: var(--purple-light, #f3f0ff);
        border-radius: 0.4rem;
        color: var(--black, #000);
        font-size: 0.85rem;
        padding: 0.25em 0.3em;
    }

    pre {
        background: var(--black, #000);
        border-radius: 0.5rem;
        color: var(--white, #fff);
        font-family: 'JetBrainsMono', monospace;
        margin: 1.5rem 0;
        padding: 0.75rem 1rem;

        code {
            background: none;
            color: inherit;
            font-size: 0.8rem;
            padding: 0;
        }
    }

    blockquote {
        border-left: 3px solid var(--gray-3, #d1d5db);
        margin: 1.5rem 0;
        padding-left: 1rem;
    }

    hr {
        border: none;
        border-top: 1px solid var(--gray-2, #e5e7eb);
        margin: 2rem 0;
    }
}
</style>